import type { PlayerApi, PlayerConfig } from '@movingimage-evp/player-library';

import {
  CloseIcon,
  IconButton,
  InputDescription,
  LinkStyleButton,
  ModalPortal,
  Note,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  TextInput,
  Tooltip,
} from '@movingimage-evp/mi-ui-component-library';
import { Player } from '@movingimage-evp/player-library';
import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfoTooltip } from 'src/components/info-tooltip';
import { GetVideoProblemCode, type Video, type VideoManager } from 'src/generated/graphql-manager';
import { useGetVideo } from 'src/manager/hooks/use-get-video';
import { secondsToMMSS } from 'src/utils';

import { getLocale } from '../../../i18n';

import styles from './video-details-modal.module.css';

type Props = {
  isOpen: boolean;
  videoId: Video['id'];
  title: string;
  videoManagerId?: VideoManager['id'];
  enableOffsetSelection?: boolean;
  offsetInSeconds?: number;
  onConfirm: (video: Video, videoManagerId?: VideoManager['id'], offsetInSeconds?: number) => void;
  onClose: () => void;
};

export function VideoDetailsModal({
  isOpen,
  videoId,
  videoManagerId,
  offsetInSeconds,
  enableOffsetSelection,
  title,
  onConfirm,
  onClose,
}: Props) {
  const locale = getLocale();
  const { t } = useTranslation();
  const [video, setVideo] = useState<Video>();
  const {
    playerConfig: videoPlayerConfig,
    video: videoData,
    loading,
    videoError: getVideoError,
  } = useGetVideo(videoManagerId, videoId);

  const [playerConfig, setPlayerConfig] = useState<PlayerConfig>();
  const [error, setError] = useState<string>();
  const [startTime, setStartTime] = useState<string | undefined>();
  const [startTimeValidationError, setStartTimeValidationError] = useState<string | undefined>();
  const [updatedOffsetInSeconds, setUpdatedOffsetInSeconds] = useState<number | undefined>(offsetInSeconds);

  const securityPolicyEnabled = videoData?.securityPolicy?.__typename === 'VideoManagerSecurityPolicy';

  const handleClose = () => {
    setPlayerConfig(undefined);
    setError(undefined);
    onClose();
  };

  const handleConfirm = () => {
    if (!video) return;

    onConfirm(video, videoManagerId, updatedOffsetInSeconds);
    handleClose();
  };

  useEffect(() => {
    if (video && offsetInSeconds) {
      setStartTime(secondsToMMSS(offsetInSeconds));
    }
  }, [video, offsetInSeconds]);

  const playerApiRef = useRef<PlayerApi | null>(null);

  const playerReadyHandler = useCallback(
    (api: PlayerApi) => {
      if (enableOffsetSelection) {
        playerApiRef.current = api;
        playerApiRef.current?.setCurrentTime(offsetInSeconds || 0);
      }
    },
    [enableOffsetSelection, offsetInSeconds]
  );

  const formatTime = (value: string) => {
    const timeParts = value.split(':').map((part) => parseInt(part, 10));

    // Format should be hh:mm
    if (!/^\d{1,3}:\d{2}$/.test(value) || timeParts.length !== 2) {
      return {
        value: 0,
        error: t('components.videoManager.videoBrowser.videoDetailsModal.startTime.validation.wrongFormat'),
      };
    }

    const [minutes, seconds] = timeParts;
    const offsetInSeconds = minutes * 60 + seconds;
    if (video && video.duration < offsetInSeconds * 1000) {
      return {
        value: 0,
        error: t('components.videoManager.videoBrowser.videoDetailsModal.startTime.validation.timeExceedsVideoLength'),
      };
    }

    return { value: offsetInSeconds };
  };

  const onStartTimeBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();

    if (value === '') {
      return;
    }

    const offsetInSeconds = formatTime(value);

    if (offsetInSeconds.error) {
      return setStartTimeValidationError(offsetInSeconds.error);
    }

    setUpdatedOffsetInSeconds(offsetInSeconds.value);
    playerApiRef.current?.setCurrentTime(offsetInSeconds.value);
    setStartTimeValidationError(undefined);
  };

  const onStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();

    setStartTime(value);

    const offsetInSeconds = formatTime(value);
    if (!offsetInSeconds.error && playerApiRef) {
      playerApiRef.current?.setCurrentTime(offsetInSeconds.value);
    }
  };

  useEffect(() => {
    if (videoPlayerConfig) setPlayerConfig(videoPlayerConfig);
  }, [videoPlayerConfig]);

  useEffect(() => {
    if (videoData) setVideo(videoData);
  }, [videoData]);

  useEffect(() => {
    if (getVideoError === GetVideoProblemCode.VIDEO_NOT_FOUND) {
      setError(t('errors.coverVideo.notFound'));
    }
  }, [getVideoError, t, videoData]);

  useEffect(() => {
    if (isOpen) {
      setError(undefined);
      setStartTimeValidationError(undefined);
    }
  }, [isOpen]);

  // Because this modal is nested inside the VideoBrowser modal, we need to prevent the ModalPortal from rendering when it's closed
  // in order to not overwrite the logic of adding the "modal-open" class to the body, as that breaks Select's UI.
  if (!isOpen) return null;

  if (!videoId || !videoManagerId) return null;

  return (
    <ModalPortal isOpen={isOpen} onClose={handleClose}>
      {loading && <Spinner />}

      {!loading && (
        <div className={styles.modal} data-testid="video-details-modal">
          <div className={styles.header}>
            <h3>{title}</h3>

            <Tooltip label={t('components.videoManager.videoBrowser.videoDetailsModal.close')}>
              <IconButton
                rounded
                onClick={handleClose}
                aria-label={t('components.videoManager.videoBrowser.videoDetailsModal.close')}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>

          <div className={styles.videoContainer}>
            <div className={styles.player}>
              {playerConfig && <Player {...playerConfig} onReady={playerReadyHandler} />}
              {error}
            </div>

            <div className={styles.content}>
              <div className={styles.info}>
                <div className={styles.item}>
                  <span>{t('components.videoManager.videoBrowser.videoDetailsModal.fileName')}</span>
                  <span data-testid="video-details-modal-file-name">{video?.title}</span>
                </div>

                <div className={styles.item}>
                  <span>{t('components.videoManager.videoBrowser.videoDetailsModal.date')}</span>

                  <span data-testid="video-details-modal-date">
                    {format(new Date(video?.createdDate || 0), 'PP', { locale })}
                  </span>
                </div>

                {enableOffsetSelection && (
                  <div className={styles.item}>
                    <div className={styles.startTimeLabel}>
                      {t('components.videoManager.videoBrowser.videoDetailsModal.startTime.label')}
                      <InfoTooltip label={t('components.videoManager.videoBrowser.videoDetailsModal.startTime.info')} />
                    </div>

                    <InputDescription
                      inputId="video-details-modal-start-time-input"
                      errorMessage={startTimeValidationError}
                    >
                      <TextInput
                        data-testid="video-details-modal-start-time-input"
                        className={styles.startTime}
                        placeholder="00:00"
                        value={startTime}
                        onChange={onStartTimeChange}
                        onBlur={onStartTimeBlur}
                      />
                    </InputDescription>
                  </div>
                )}
              </div>
            </div>
          </div>

          {securityPolicyEnabled && (
            <Note data-testid="security-policy-info" variant="primary" className={styles.securityPolicyInfo}>
              <div className={styles.securityPolicyInfoContent}>
                {t('components.videoManager.videoBrowser.videoDetailsModal.securityPolicyInfo.text')}

                <LinkStyleButton>
                  <a
                    href={t('components.videoManager.videoBrowser.videoDetailsModal.securityPolicyInfo.link.url')}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.link}
                    data-testid="help-link-documentation"
                  >
                    {t('components.videoManager.videoBrowser.videoDetailsModal.securityPolicyInfo.link.text')}
                  </a>
                </LinkStyleButton>
              </div>
            </Note>
          )}

          <div className={styles.actions}>
            <PrimaryButton
              data-testid="video-details-modal-confirm-button"
              disabled={!video || error !== undefined || startTimeValidationError !== undefined}
              onClick={handleConfirm}
            >
              {t('components.videoManager.videoBrowser.videoDetailsModal.buttons.confirm')}
            </PrimaryButton>

            <SecondaryButton data-testid="video-details-modal-cancel-button" onClick={handleClose}>
              {t('components.videoManager.videoBrowser.videoDetailsModal.buttons.cancel')}
            </SecondaryButton>
          </div>
        </div>
      )}
    </ModalPortal>
  );
}
